import React, { useState } from 'react';
import { HeadphonesAffordance } from '../../Common/DesignComponents.jsx';
import useAudioEngine from '../../../audio/useAudioEngine.js';
import formatClipTitle from '../../../utils/formatClipTitle.js';
import '../../../styles/theme.css';

const SAMPLE_CLIPS = [
  { id: 'sfx_screaming_goat', title: 'Screaming Goat', category: 'Funny' },
  { id: 'sfx_metal_pipe_falling', title: 'Metal Pipe Falling', category: 'Impact' },
  { id: 'sfx_airhorn_triple', title: 'Triple Airhorn', category: 'Meme' },
  { id: 'sfx_sad_trombone', title: 'Sad Trombone', category: 'Reaction' },
  { id: 'sfx_wilhelm_scream', title: 'Wilhelm Scream', category: 'Vocal' },
  { id: 'sfx_record_scratch', title: 'Record Scratch', category: 'Transition' },
];

export default function DriftPlayer({
  phase = 'step_0',
  modeState = {},
  playerId = '',
  onSubmitStep = () => {},
  onSubmitVote = () => {},
  hasSubmitted = false,
}) {
  const [phraseText, setPhraseText] = useState('');
  const [selectedClipId, setSelectedClipId] = useState(SAMPLE_CLIPS[0].id);
  const [auditioningClipId, setAuditioningClipId] = useState(null);
  const [submitted, setSubmitted] = useState(hasSubmitted);

  const [funniestTarget, setFunniestTarget] = useState('');
  const [accurateTarget, setAccurateTarget] = useState('');

  const audio = useAudioEngine('player');

  const currentPhase = phase || modeState.currentPhase || 'step_0';
  const isStepPhase = currentPhase.startsWith('step_') || currentPhase === 'answering';
  const isGrandReveal = currentPhase === 'grand_reveal';
  const isVoting = currentPhase === 'voting';
  const isResults = currentPhase === 'results' || currentPhase === 'game_over';

  const entryIndex = modeState.entryIndex || 0;
  const stepType = entryIndex % 2 === 0 ? 'text' : 'audio';

  const previousEntry = modeState.assignedPreviousEntry || modeState.previousEntry || null;

  const handleAudition = async (clip) => {
    setAuditioningClipId(clip.id);
    try {
      if (audio.audition) {
        await audio.audition(clip);
      } else if (audio.play) {
        await audio.play(clip.id, { gain: 0.3 });
      }
    } catch {
      // Audition catch
    } finally {
      setTimeout(() => setAuditioningClipId(null), 2500);
    }
  };

  const handleSubmitText = (e) => {
    e.preventDefault();
    if (!phraseText.trim() || submitted) return;

    setSubmitted(true);
    onSubmitStep({
      type: 'text',
      text: phraseText.trim(),
    });
  };

  const handleSubmitAudio = (e) => {
    e.preventDefault();
    if (!selectedClipId || submitted) return;

    const clipObj = SAMPLE_CLIPS.find(c => c.id === selectedClipId) || SAMPLE_CLIPS[0];
    setSubmitted(true);
    onSubmitStep({
      type: 'audio',
      clipId: clipObj.id,
      clipTitle: clipObj.title,
    });
  };

  const handleSubmitVotes = (e) => {
    e.preventDefault();
    if (submitted) return;

    setSubmitted(true);
    onSubmitVote({
      funniest: funniestTarget,
      accurate: accurateTarget,
    });
  };

  const chains = modeState.chains || [];
  const eligibleEntries = [];
  chains.forEach((chain, cIdx) => {
    (chain.entries || []).forEach((entry, eIdx) => {
      if (entry.authorId !== playerId) {
        eligibleEntries.push({
          key: `${cIdx}_${eIdx}`,
          chainIndex: cIdx,
          entryIndex: eIdx,
          authorName: entry.authorName || 'Player',
          label: entry.type === 'text' ? `"${entry.text}"` : `Sound: ${entry.clipTitle || entry.clipId}`,
        });
      }
    });
  });

  return (
    <div
      data-testid="drift-player"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        flex: 1,
        color: 'var(--sd-ink, #EDEFF2)',
        fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
      }}
    >
      {/* Headphones affordance during step selection */}
      {isStepPhase && !submitted && <HeadphonesAffordance visible={true} />}

      {/* Submitted confirmation waiting view */}
      {submitted && (
        <div
          style={{
            textAlign: 'center',
            marginTop: 'auto',
            marginBottom: 'auto',
            padding: '36px 24px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>✅</div>
          <h2
            data-testid="submission-confirmed"
            style={{
              fontSize: '1.6rem',
              color: 'var(--sd-sage, #8FC7A0)',
              margin: '0 0 8px 0',
              fontWeight: 800,
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            }}
          >
            Submission Received!
          </h2>
          <p style={{ color: 'var(--sd-muted, #79838D)', fontSize: '1rem', margin: 0 }}>
            Waiting for other players to finish their turn...
          </p>
        </div>
      )}

      {/* Step Phase - Text Step */}
      {isStepPhase && !submitted && stepType === 'text' && (
        <div
          style={{
            padding: '24px 20px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-teal, #3ACFCF)',
            fontWeight: 600,
            display: 'inline-block',
            marginBottom: '12px',
          }}>
            STEP {entryIndex + 1} — TEXT PHRASE
          </span>

          {entryIndex === 0 ? (
            <h3 style={{ fontSize: '1.25rem', color: 'var(--sd-ink, #EDEFF2)', fontWeight: 800, margin: '0 0 16px 0' }}>
              ✍️ Write an initial phrase to start this telephone chain!
            </h3>
          ) : (
            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '1.15rem', color: 'var(--sd-ink, #EDEFF2)', fontWeight: 800, margin: '0 0 8px 0' }}>
                🎧 Listen to previous sound clip and guess what it is:
              </h3>
              {previousEntry && (
                <div
                  style={{
                    background: 'var(--sd-panel-2, #0E1116)',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    border: '1px solid var(--sd-line-2, #1C2229)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    minWidth: 0,
                  }}
                >
                  <span
                    title={formatClipTitle(previousEntry.clipTitle || previousEntry.clipId, 100)}
                    style={{
                      color: 'var(--sd-teal, #3ACFCF)',
                      fontWeight: 700,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      minWidth: 0,
                      flex: '1 1 0',
                    }}
                  >
                    🔊 {formatClipTitle(previousEntry.clipTitle || previousEntry.clipId || 'Previous Audio Clip')}
                  </span>
                  <button
                    type="button"
                    data-testid="audition-prev-clip-btn"
                    onClick={() => handleAudition({ id: previousEntry.clipId || 'prev' })}
                    style={{
                      padding: '0 16px',
                      minHeight: '44px',
                      borderRadius: '10px',
                      background: 'var(--sd-teal, #3ACFCF)',
                      color: 'var(--sd-ground, #08090B)',
                      border: 'none',
                      fontWeight: 700,
                      fontSize: '13px',
                      cursor: 'pointer',
                      fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                    }}
                  >
                    {auditioningClipId === (previousEntry.clipId || 'prev') ? 'Playing... 🔊' : 'Listen 🎧'}
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmitText} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <input
              data-testid="drift-text-input"
              placeholder="e.g. A spooky ghost haunting a house"
              value={phraseText}
              onChange={(e) => setPhraseText(e.target.value)}
              maxLength={80}
              style={{
                width: '100%',
                padding: '14px 16px',
                fontSize: '15px',
                borderRadius: '12px',
                border: '1px solid var(--sd-line-2, #1C2229)',
                background: 'var(--sd-panel-2, #0E1116)',
                color: 'var(--sd-ink, #EDEFF2)',
                boxSizing: 'border-box',
                outline: 'none',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
              }}
            />

            <button
              data-testid="submit-text-btn"
              type="submit"
              disabled={!phraseText.trim()}
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                borderRadius: '14px',
                background: !phraseText.trim() ? 'var(--sd-panel-2, #0E1116)' : 'var(--sd-teal, #3ACFCF)',
                border: !phraseText.trim() ? '1px solid var(--sd-line, #1A1F25)' : '1px solid var(--sd-teal, #3ACFCF)',
                color: !phraseText.trim() ? 'var(--sd-dim, #6E7982)' : 'var(--sd-ground, #08090B)',
                fontWeight: 700,
                fontSize: '15px',
                cursor: !phraseText.trim() ? 'not-allowed' : 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                transition: 'all 0.15s ease',
              }}
            >
              Submit Phrase 🚀
            </button>
          </form>
        </div>
      )}

      {/* Step Phase - Audio Step */}
      {isStepPhase && !submitted && stepType === 'audio' && (
        <div
          style={{
            padding: '24px 20px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-rose, #E4738A)',
            fontWeight: 600,
            display: 'inline-block',
            marginBottom: '12px',
          }}>
            STEP {entryIndex + 1} — SOUND CLIP
          </span>

          <div style={{ marginBottom: '16px' }}>
            <span style={{
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
              fontSize: '11px',
              color: 'var(--sd-dim, #6E7982)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 600
            }}>
              Previous Phrase to Match:
            </span>
            <div
              data-testid="prev-phrase-card"
              style={{
                background: 'var(--sd-panel-2, #0E1116)',
                padding: '14px 18px',
                borderRadius: '12px',
                border: '1px solid var(--sd-teal, #3ACFCF)',
                fontSize: '1.2rem',
                fontWeight: 800,
                color: 'var(--sd-teal, #3ACFCF)',
                marginTop: '6px',
              }}
            >
              "{previousEntry?.text || 'A mystery phrase'}"
            </div>
          </div>

          <h3 style={{ fontSize: '1.1rem', color: 'var(--sd-ink, #EDEFF2)', fontWeight: 800, margin: '0 0 12px 0' }}>
            🔊 Pick the sound clip that best matches this phrase:
          </h3>

          <form onSubmit={handleSubmitAudio} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div
              data-testid="drift-clip-picker"
              style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            >
              {SAMPLE_CLIPS.map((clip) => (
                <div
                  key={clip.id}
                  data-testid={`clip-option-${clip.id}`}
                  onClick={() => setSelectedClipId(clip.id)}
                  style={{
                    background: selectedClipId === clip.id ? 'rgba(58, 207, 207, 0.08)' : 'var(--sd-panel-2, #0E1116)',
                    border: selectedClipId === clip.id ? '1px solid var(--sd-teal, #3ACFCF)' : '1px solid var(--sd-line-2, #1C2229)',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    minHeight: '44px',
                  }}
                >
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <div
                      title={formatClipTitle(clip, 100)}
                      style={{
                        fontWeight: 700,
                        color: 'var(--sd-ink, #EDEFF2)',
                        fontSize: '1rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatClipTitle(clip)}
                    </div>
                    <span style={{
                      fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
                      fontSize: '10px',
                      color: 'var(--sd-dim, #6E7982)',
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                    }}>
                      {clip.category}
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAudition(clip);
                    }}
                    style={{
                      padding: '0 14px',
                      minHeight: '44px',
                      minWidth: '44px',
                      borderRadius: '10px',
                      background: 'var(--sd-panel, #0D1015)',
                      border: '1px solid var(--sd-line, #1A1F25)',
                      color: 'var(--sd-dim, #6E7982)',
                      fontWeight: 600,
                      fontSize: '12px',
                      cursor: 'pointer',
                    }}
                  >
                    {auditioningClipId === clip.id ? 'Playing... 🔊' : 'Audition 🎧'}
                  </button>
                </div>
              ))}
            </div>

            <button
              data-testid="submit-audio-btn"
              type="submit"
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                borderRadius: '14px',
                background: 'var(--sd-teal, #3ACFCF)',
                border: '1px solid var(--sd-teal, #3ACFCF)',
                color: 'var(--sd-ground, #08090B)',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                transition: 'all 0.15s ease',
              }}
            >
              Submit Sound Clip 🔊
            </button>
          </form>
        </div>
      )}

      {/* Grand Reveal Phase - Device Muted */}
      {isGrandReveal && (
        <div
          style={{
            textAlign: 'center',
            marginTop: 'auto',
            marginBottom: 'auto',
            padding: '36px 24px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div
            data-testid="player-muted-indicator"
            style={{
              background: 'rgba(228, 115, 138, 0.12)',
              border: '1px solid var(--sd-rose, #E4738A)',
              color: 'var(--sd-rose, #E4738A)',
              padding: '10px 16px',
              borderRadius: '12px',
              fontSize: '0.9rem',
              fontWeight: 700,
              marginBottom: '20px',
              fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            }}
          >
            🔇 Device Muted for Host TV Playback
          </div>

          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>📺</div>
          <h2 style={{ fontSize: '1.6rem', color: 'var(--sd-ink, #EDEFF2)', fontWeight: 800, margin: '0 0 8px 0' }}>
            Look at the TV Screen!
          </h2>
          <p style={{ color: 'var(--sd-muted, #79838D)', fontSize: '1rem', margin: 0 }}>
            The Grand Reveal is playing back all telephone chains!
          </p>
        </div>
      )}

      {/* Voting Phase */}
      {isVoting && !submitted && (
        <div
          data-testid="drift-player-voting"
          style={{
            padding: '24px 20px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '16px',
          }}
        >
          <span style={{
            fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace",
            fontSize: '11px',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--sd-amber, #E0A458)',
            fontWeight: 600,
            display: 'inline-block',
            marginBottom: '14px',
          }}>
            🗳️ VOTE ON DRIFT MOMENTS
          </span>

          <form onSubmit={handleSubmitVotes} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', display: 'block', marginBottom: '8px' }}>
                1. Funniest Drift Moment:
              </label>
              <select
                data-testid="vote-funniest-select"
                value={funniestTarget}
                onChange={(e) => setFunniestTarget(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  background: 'var(--sd-panel-2, #0E1116)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                }}
              >
                <option value="">-- Choose Funniest Moment --</option>
                {eligibleEntries.map((e) => (
                  <option key={e.key} value={e.key}>
                    Chain #{e.chainIndex + 1} Step {e.entryIndex + 1}: {e.label} (by {e.authorName})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.95rem', fontWeight: 700, color: 'var(--sd-ink, #EDEFF2)', display: 'block', marginBottom: '8px' }}>
                2. Most Accurate Guess:
              </label>
              <select
                data-testid="vote-accurate-select"
                value={accurateTarget}
                onChange={(e) => setAccurateTarget(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  background: 'var(--sd-panel-2, #0E1116)',
                  color: 'var(--sd-ink, #EDEFF2)',
                  border: '1px solid var(--sd-line-2, #1C2229)',
                  fontSize: '14px',
                  outline: 'none',
                  fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                }}
              >
                <option value="">-- Choose Most Accurate Guess --</option>
                {eligibleEntries.map((e) => (
                  <option key={e.key} value={e.key}>
                    Chain #{e.chainIndex + 1} Step {e.entryIndex + 1}: {e.label} (by {e.authorName})
                  </option>
                ))}
              </select>
            </div>

            <button
              data-testid="submit-votes-btn"
              type="submit"
              style={{
                width: '100%',
                height: '54px',
                minHeight: '44px',
                borderRadius: '14px',
                background: 'var(--sd-amber, #E0A458)',
                border: '1px solid var(--sd-amber, #E0A458)',
                color: 'var(--sd-ground, #08090B)',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                fontFamily: "var(--sd-font-ui, 'Plus Jakarta Sans'), system-ui, sans-serif",
                transition: 'all 0.15s ease',
              }}
            >
              Submit Votes 🗳️
            </button>
          </form>
        </div>
      )}

      {/* Results Phase */}
      {isResults && (
        <div
          style={{
            textAlign: 'center',
            marginTop: 'auto',
            marginBottom: 'auto',
            padding: '36px 24px',
            background: 'var(--sd-panel, #0D1015)',
            border: '1px solid var(--sd-line, #1A1F25)',
            borderRadius: '20px',
          }}
        >
          <div style={{ fontSize: '3rem', marginBottom: '12px' }}>🏆</div>
          <h2 style={{ fontSize: '1.8rem', color: 'var(--sd-amber, #E0A458)', fontWeight: 800, margin: '0 0 8px 0', fontFamily: "var(--sd-font-mono, 'IBM Plex Mono'), monospace" }}>
            Game Over!
          </h2>
          <p style={{ color: 'var(--sd-muted, #79838D)', fontSize: '1.1rem', margin: 0 }}>
            Check the TV screen for the final standings and awards!
          </p>
        </div>
      )}
    </div>
  );
}
